import React, { useEffect } from "react";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { SingleValue } from "react-select";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../api/useCategories";

export const useCategoryInput = (selectedEntry: { failureCategoryId: string | null }) => {
  const category = useCategories("FAILURE_CATEGORY");
  const createCategory = useCreateCategory("FAILURE_CATEGORY");
  const updateCategory = useUpdateCategory("FAILURE_CATEGORY");
  const deleteCategory = useDeleteCategory("FAILURE_CATEGORY");
  const [dirty, setDirty] = React.useState(false);
  const categoryOptions = React.useMemo(
    () => buildOptions([...category.map(e => ({ label: e, value: e }))]),
    [category],
  );

  const categoryDefaultValue = React.useMemo(
    () =>
      category
        .filter(id => selectedEntry?.failureCategoryId === id)
        .map(id => ({ value: id, label: id }))[0] ?? null,
    [selectedEntry?.failureCategoryId, category],
  );

  const [selectedCategory, setSelectedCategory] = React.useState<SingleValue<BaseOption>>(
    categoryDefaultValue?.label === "" ? null : categoryDefaultValue,
  );

  useEffect(() => {
    if (selectedCategory?.value && !category.includes(selectedCategory.value)) {
      setSelectedCategory(null);
    }
  }, [selectedCategory, category]);

  const handleCategoryChanged = React.useCallback(
    (value: SingleValue<BaseOption>) => {
      setSelectedCategory(value);
      setDirty(value !== categoryDefaultValue);
    },
    [categoryDefaultValue],
  );

  const handleCategoryCreated = React.useCallback(
    async (newCategory: string) => {
      await createCategory(newCategory);
      setSelectedCategory({ value: newCategory, label: newCategory });
      setDirty(true);
    },
    [createCategory],
  );

  const handleCategoryDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (option.value) {
        await deleteCategory(option.value);
        if (selectedCategory?.value === option.value) setSelectedCategory(null);
        setDirty(true);
      }
    },
    [deleteCategory, selectedCategory?.value],
  );

  const handleCategoryEdited = React.useCallback(
    async ({ value }: BaseOption, label: string) => {
      if (value) {
        await updateCategory(value, label);
        if (selectedCategory?.value === value) {
          setSelectedCategory({ label, value });
        }
        setDirty(true);
      }
    },
    [updateCategory, selectedCategory],
  );

  const resetValues = React.useCallback(() => {
    setSelectedCategory(categoryDefaultValue);
    setDirty(false);
  }, [categoryDefaultValue]);

  return {
    category,
    categoryOptions,
    categoryDefaultValue,
    selectedCategory,
    dirty,
    resetValues,
    handleCategoryChanged,
    handleCategoryCreated,
    handleCategoryDeleted,
    handleCategoryEdited,
  };
};
